import {
    X,
    Mail,
    Phone,
    MapPin,
    ClipboardList,
    Users,
    CheckCircle2,
    FileText,
    Eye,
    MessageSquare,
    Ban,
  } from "lucide-react";
  import { Badge } from "./ui/badge";
  import { Button } from "./ui/button";
import moment from "moment";
import { TwoButtonAlert } from "Common/twoButtonAlert";
import { IoTrashOutline } from "react-icons/io5";
import { useState } from "react";
import { IoPlayOutline } from "react-icons/io5";
import { CallAPIPostPromise } from "Common/commaon";
import { IoWarningOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
  
  interface Homeowner {
    id: string;
    userId: string;
    name: string;
    email: string;
    phone: string;
    city: string;
    state: string;
    zipCode: string;
    profileImage: string;
    totalLeads: number;
    activeLeads: number;
    completedProjects: number;
    contractsCreated: number;
    contractorContacted: number;
    contractsSigned: number;
    status: "active" | "blocked" | "pending";
    joinedDate: string;
    lastActive: string;
  }
  
  interface HomeownerDetailModalProps {
    homeowner: Homeowner;
    goBack:any,
    onClose: () => void;
  }
  
  export function HomeownerDetailModal({
    homeowner,
    onClose,goBack,
  }: HomeownerDetailModalProps) {
    const getStatusColor = (status: string) => {
      switch (status) {
        case "active":
          return "bg-green-50 text-green-700 border-green-200";
        case "blocked":
          return "bg-red-50 text-red-700 border-red-200";
        case "pending":
          return "bg-yellow-50 text-yellow-700 border-yellow-200";
        default:
          return "bg-gray-50 text-gray-700 border-gray-200";
      }
    };
    const [loading,setLoading]=useState(false)
    const [alertDetails,setAlertDetails]= useState({
      status: false,
      title: "Warning!",
      message: "You’re about to delete your account forever,",
      icon: IoTrashOutline,
      button1Text: "Keep it",
      button2Text: "Delete",
      button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
      button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
      onButton1Click: () => console.log("Saved "),
      onButton2Click: () => console.log("Deleted "),
    })

    const navigate = useNavigate()
    const getStatusLabel = (status: string) => {
      switch (status) {
        case "active":
          return "Active";
        case "blocked":
          return "Blocked";
        case "pending":
          return "Pending Verification";
        default:
          return status;
      }
    };
  
    return (
      <div style={{
        zIndex:10000
      }} className="fixed inset-0 flex items-center justify-center bg-black/50 p-4 homeowner-detail-modal">
        <div
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="text-xl font-semibold text-gray-900">
              Homeowner Details
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 bg-transparent border-0 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
  
          {/* Content */}
          <div className="p-6 space-y-6">
      

<div className="flex items-start gap-4 pb-6 border-b border-gray-200">
  {homeowner.profileImage ? (
    <img
      src={homeowner.profileImage}
      alt={homeowner.name}
      className="w-20 h-20 rounded-full object-cover border-2 border-gray-100"
    />
  ) : (
    <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 text-xl font-semibold border-2 border-gray-100">
      {homeowner.name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()}
    </div>
  )}

  <div className="flex-1">
    <div className="flex items-center gap-3 mb-2">
      <div className="text-2xl font-semibold text-gray-900">
        {homeowner.name}
      </div>

      <Badge
        variant="secondary"
        className="bg-blue-50 text-blue-700 border-blue-200"
      >
        Homeowner
      </Badge>

      <Badge
        variant="outline"
        className={`${getStatusColor(homeowner.status)}`}
      >
        {getStatusLabel(homeowner.status)}
      </Badge>
    </div>

    <div className="text-gray-500 mb-3">ID: {homeowner.userId}</div>

    <div className="flex gap-2">
      <div className="text-sm text-gray-500">
        Joined: {homeowner.joinedDate ? moment(homeowner.joinedDate).format("MMM D, YYYY") : "N/A"}
      </div>

      <span className="text-gray-300">•</span>

      <div className="text-sm text-gray-500">
        Last active: {homeowner.lastActive ? moment(homeowner.lastActive).fromNow() : "N/A"}
      </div>
    </div>
  </div>
</div>
  
            {/* Contact Information */}
            <div className="space-y-3">
              <div className="font-semibold text-gray-900">Contact Information</div>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Email</div>
                    <div className="text-sm text-gray-900">{homeowner.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Phone</div>
                    <div className="text-sm text-gray-900">{homeowner.phone}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Location</div>
                    <div className="text-sm text-gray-900">
                      {homeowner.city}, {homeowner.state} {homeowner.zipCode}
                    </div>
                  </div>
                </div>
              </div>
            </div>
  
            {/* Activity Statistics */}
            <div className="space-y-3">
              <div className="font-semibold text-gray-900">Activity Statistics</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardList className="w-4 h-4 text-gray-500" />
                    <div className="text-sm text-gray-500">Total Leads</div>
                  </div>
                  <div className="text-2xl font-semibold text-gray-900">
                    {homeowner.totalLeads}
                  </div>
                </div>
  
                <div className="bg-blue-50 rounded-lg px-4 py-3 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardList className="w-4 h-4 text-blue-600" />
                    <div className="text-sm text-blue-600">Active Leads</div>
                  </div>
                  <div className="text-2xl font-semibold text-blue-700">
                    {homeowner.activeLeads}
                  </div>
                </div>
  
                <div className="bg-green-50 rounded-lg px-4 py-3 border border-green-100">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <div className="text-sm text-green-600">Completed Projects</div>
                  </div>
                  <div className="text-2xl font-semibold text-green-700">
                    {homeowner.completedProjects}
                  </div>
                </div>
  
                <div className="bg-purple-50 rounded-lg px-4 py-3 border border-purple-100">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-purple-600" />
                    <div className="text-sm text-purple-600">Contracts Created</div>
                  </div>
                  <div className="text-2xl font-semibold text-purple-700">
                    {homeowner.contractsCreated}
                  </div>
                </div>
  
                <div className="bg-orange-50 rounded-lg px-4 py-3 border border-orange-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-orange-600" />
                    <div className="text-sm text-orange-600">Contractors Contacted</div>
                  </div>
                  <div className="text-2xl font-semibold text-orange-700">
                    {homeowner.contractorContacted}
                  </div>
                </div>
  
                <div className="bg-indigo-50 rounded-lg px-4 py-3 border border-indigo-100">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    <div className="text-sm text-indigo-600">Contracts Signed</div>
                  </div>
                  <div className="text-2xl font-semibold text-indigo-700">
                    {homeowner.contractsSigned}
                  </div>
                </div>
              </div>
            </div>
  
            {/* Actions */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <div className="font-semibold text-gray-900">Admin Actions</div>
              <div className="flex flex-wrap gap-2">
               
    
    
              <Button
  variant="outline"
  onClick={() => {
    setAlertDetails({
      status: true,
      title: "Permanent Delete",
      message: `This will permanently delete ${homeowner.email}'s account. This action cannot be undone and all data (projects, contracts, history) will be lost forever.`,
      icon: IoWarningOutline, // better than play icon 😅

      button1Text: "Cancel",
      button2Text: "Delete Permanently",

      button1Style:
        "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",

      button2Style:
        "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",

      onButton1Click: () => {},

      onButton2Click: async () => {
        try {
          setLoading(true);

          await CallAPIPostPromise(`/admin/deleteAccount`, {
            id: homeowner.id,
          });
goBack()
        } catch (e) {
          console.log(e);
        } finally {
          setLoading(false);
        }
      },
    });
  }}
  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
>
  <Ban className="h-4 w-4" />
  Delete Permanently
</Button>
              </div>
            </div>
          </div>
        </div>
        <TwoButtonAlert setDetails={setAlertDetails}  details={alertDetails}/>
      </div>
    );
  }
  
